import type { HsFrustumDataView, HsFrustumView, HsGeometryComponentView } from '@sibe/hyperscene';
import { hsNormalizeGeometryId } from './decoded-geometry-store.class';

const IDENTITY_MATRIX: readonly number[] = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/** Frustum entry kinds, as the API spells them in lower case. */
export type HsFrustumGeometryKind = 'mesh' | 'line' | 'pointcloud';

/** Lower-cased id, or `'none'` for a missing or blank one. Render keys use it so they stay stable. */
export function hsNormalizeOptionalId(value: unknown): string {
  if (typeof value !== 'string') {
    return 'none';
  }
  const normalized: string = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : 'none';
}

/** A 16-element column-major matrix with non-finite values replaced by the identity's. */
export function hsToMatrix16OrIdentity(value: unknown): number[] {
  if (!Array.isArray(value) || value.length !== 16) {
    return IDENTITY_MATRIX.slice();
  }
  const matrix: number[] = new Array<number>(16);
  for (let i: number = 0; i < 16; i++) {
    const item: unknown = value[i];
    matrix[i] = typeof item === 'number' && Number.isFinite(item) ? item : IDENTITY_MATRIX[i];
  }
  return matrix;
}

/**
 * One drawn instance of a geometry: node, geometry, tree and both materials. The same geometry under
 * another node or material is another instance, so it gets its own key.
 */
export function hsMakeFrustumRenderKey(component: HsGeometryComponentView, geometryId: string): string {
  const nodeId: string = hsNormalizeOptionalId(component.nodeId);
  return [
    nodeId !== 'none' ? nodeId : 'node:unknown',
    geometryId,
    hsNormalizeOptionalId(component.treeId),
    hsNormalizeOptionalId(component.sceneMaterialId),
    hsNormalizeOptionalId(component.originalMaterialId),
  ].join('|');
}

/** The node id a render key starts with. */
export function hsFrustumRenderKeyNodeId(renderKey: string): string {
  const separatorIndex: number = renderKey.indexOf('|');
  return separatorIndex >= 0 ? renderKey.slice(0, separatorIndex) : renderKey;
}

/** The scene material overrides the original one; an empty string means neither is set. */
export function hsPickFrustumMaterialId(component: HsGeometryComponentView): string {
  const sceneMaterialId: string = hsNormalizeOptionalId(component.sceneMaterialId);
  if (sceneMaterialId !== 'none') {
    return sceneMaterialId;
  }
  const originalMaterialId: string = hsNormalizeOptionalId(component.originalMaterialId);
  return originalMaterialId !== 'none' ? originalMaterialId : '';
}

/** True when a mesh entry removes a node and adds it back, which is how the API swaps LOD levels. */
export function hsFrustumHasMeshReplacement(view: HsFrustumView): boolean {
  return (view.frustumData ?? []).some((entry: HsFrustumDataView) => {
    if (entry.type?.toLowerCase() !== 'mesh') {
      return false;
    }
    const deleted: Set<string> = new Set(entry.toDelete ?? []);
    return entry.toAdd?.some((item: HsGeometryComponentView) => deleted.has(item.nodeId)) ?? false;
  });
}

/** Number of download requests for the groups when each is split into batches of its own size. */
export function hsCountGeometryDownloadBatches(
  groups: readonly { geometryIds: readonly string[]; batchSize?: number }[],
  defaultBatchSize: number
): number {
  return groups.reduce((total: number, group: { geometryIds: readonly string[]; batchSize?: number }) => {
    const uniqueCount: number = new Set(group.geometryIds).size;
    const batchSize: number = group.batchSize ?? defaultBatchSize;
    return total + (uniqueCount === 0 ? 0 : Math.ceil(uniqueCount / batchSize));
  }, 0);
}

/**
 * The instances of one geometry kind the server has sent for the current Frustum session. Fields are
 * plain records so hosts can read them per frame without allocation; a delta replaces them as a whole.
 */
export class HsFrustumGeometrySet {
  public renderKeys: string[] = [];
  public geometryIdByRenderKey: Record<string, string> = {};
  public materialIdByRenderKey: Record<string, string> = {};
  /** Only meshes carry per-instance textures; other kinds keep this empty. */
  public textureIdByRenderKey: Record<string, string> = {};
  public worldMatricesByRenderKey: Record<string, number[]> = {};
  /** Unique geometry ids of `renderKeys`, in first-seen order. */
  public geometryIds: string[] = [];

  public constructor(public readonly tracksTextures: boolean = false) {}

  public clear(): void {
    this.renderKeys = [];
    this.geometryIdByRenderKey = {};
    this.materialIdByRenderKey = {};
    this.textureIdByRenderKey = {};
    this.worldMatricesByRenderKey = {};
    this.geometryIds = [];
  }

  /** Unique geometry ids of the given render keys. */
  public collectGeometryIds(renderKeys: readonly string[]): string[] {
    const ids: Set<string> = new Set<string>();
    for (const renderKey of renderKeys) {
      const geometryId: string | undefined = this.geometryIdByRenderKey[renderKey];
      if (geometryId !== undefined && geometryId.length > 0) {
        ids.add(geometryId);
      }
    }
    return Array.from(ids);
  }

  /** Applies the entries to copies of the current records and swaps them in; returns the keys before. */
  public applyEntries(entries: readonly HsFrustumDataView[], resetSession: boolean): Set<string> {
    const previous: Set<string> = new Set<string>(resetSession ? [] : this.renderKeys);
    const renderKeys: Set<string> = new Set<string>(previous);
    const geometryIds: Record<string, string> = resetSession ? {} : { ...this.geometryIdByRenderKey };
    const materialIds: Record<string, string> = resetSession ? {} : { ...this.materialIdByRenderKey };
    const textureIds: Record<string, string> = resetSession ? {} : { ...this.textureIdByRenderKey };
    const matrices: Record<string, number[]> = resetSession ? {} : { ...this.worldMatricesByRenderKey };
    for (const entry of entries) {
      const nodesToDelete: Set<string> = new Set(
        (entry.toDelete ?? [])
          .map((rawNodeId: string) => hsNormalizeGeometryId(rawNodeId))
          .filter((nodeId: string) => nodeId.length > 0)
      );
      if (nodesToDelete.size > 0) {
        for (const renderKey of renderKeys) {
          if (!nodesToDelete.has(hsFrustumRenderKeyNodeId(renderKey))) {
            continue;
          }
          renderKeys.delete(renderKey);
          Reflect.deleteProperty(geometryIds, renderKey);
          Reflect.deleteProperty(materialIds, renderKey);
          Reflect.deleteProperty(textureIds, renderKey);
          Reflect.deleteProperty(matrices, renderKey);
        }
      }
      for (const component of entry.toAdd ?? []) {
        const geometryId: string = hsNormalizeGeometryId(component.geometryId ?? '');
        if (geometryId.length === 0) {
          continue;
        }
        const renderKey: string = hsMakeFrustumRenderKey(component, geometryId);
        renderKeys.add(renderKey);
        geometryIds[renderKey] = geometryId;
        materialIds[renderKey] = hsPickFrustumMaterialId(component);
        if (this.tracksTextures && component.textureId !== null && component.textureId !== undefined) {
          textureIds[renderKey] = hsNormalizeGeometryId(component.textureId);
        } else {
          Reflect.deleteProperty(textureIds, renderKey);
        }
        matrices[renderKey] = hsToMatrix16OrIdentity(component.worldMatrix);
      }
    }
    this.renderKeys = Array.from(renderKeys);
    this.geometryIdByRenderKey = geometryIds;
    this.materialIdByRenderKey = materialIds;
    this.textureIdByRenderKey = textureIds;
    this.worldMatricesByRenderKey = matrices;
    this.geometryIds = this.collectGeometryIds(this.renderKeys);
    return previous;
  }
}

/** What one Frustum response changed. */
export interface HsFrustumDelta {
  /** The response belongs to a session the state had not seen; everything before it was dropped. */
  newSession: boolean;
  /** Render keys of each kind before the response, for telling new instances from kept ones. */
  previousRenderKeys: Record<HsFrustumGeometryKind, Set<string>>;
}

/**
 * The scene content the server reported for a Frustum session: which geometry instances to draw, with
 * their materials, textures and matrices. The server sends deltas against what it sent before in the
 * same session, so the state keeps the session id and starts over when it changes.
 */
export class HsFrustumSceneState {
  public readonly meshes: HsFrustumGeometrySet = new HsFrustumGeometrySet(true);
  public readonly lines: HsFrustumGeometrySet = new HsFrustumGeometrySet();
  public readonly pointClouds: HsFrustumGeometrySet = new HsFrustumGeometrySet();
  /** Session of the last applied response, or null before the first one. */
  public appliedSessionId: string | null = null;

  public set(kind: HsFrustumGeometryKind): HsFrustumGeometrySet {
    return kind === 'mesh' ? this.meshes : kind === 'line' ? this.lines : this.pointClouds;
  }

  public applyFrustum(view: HsFrustumView, sessionId: string): HsFrustumDelta {
    const newSession: boolean = this.appliedSessionId !== sessionId;
    this.appliedSessionId = sessionId;
    const entriesByKind: Record<HsFrustumGeometryKind, HsFrustumDataView[]> = { mesh: [], line: [], pointcloud: [] };
    for (const entry of view.frustumData ?? []) {
      const kind: string = (entry.type ?? '').trim().toLowerCase();
      if (kind === 'mesh' || kind === 'line' || kind === 'pointcloud') {
        entriesByKind[kind].push(entry);
      }
    }
    return {
      newSession,
      previousRenderKeys: {
        mesh: this.meshes.applyEntries(entriesByKind.mesh, newSession),
        line: this.lines.applyEntries(entriesByKind.line, newSession),
        pointcloud: this.pointClouds.applyEntries(entriesByKind.pointcloud, newSession),
      },
    };
  }

  public clear(): void {
    this.meshes.clear();
    this.lines.clear();
    this.pointClouds.clear();
    this.appliedSessionId = null;
  }
}
